import type { JSX } from 'react';
import { useTranslation } from 'react-i18next';
import type { LigneRapportOeuvres, RapportOeuvres } from '../../api/oeuvres.js';
import styles from './RapportRepriseVue.module.css';

const CLE = 'oeuvres.importExport.rapport';

function Lignes({
  titre,
  lignes,
  classe,
}: {
  readonly titre: string;
  readonly lignes: readonly LigneRapportOeuvres[];
  readonly classe: string | undefined;
}): JSX.Element | null {
  const { t } = useTranslation('regie');
  if (lignes.length === 0) return null;
  return (
    <section className={styles.lignes}>
      <h4>{titre}</h4>
      <ul className={classe}>
        {lignes.map((ligne, index) => (
          <li key={index}>
            <strong>{t(`${CLE}.ligne`, { ligne: ligne.ligne, libelle: ligne.libelle })}</strong>
            <ul>
              {ligne.messages.map((message, rang) => (
                <li key={rang}>{message}</li>
              ))}
            </ul>
          </li>
        ))}
      </ul>
    </section>
  );
}

/** Le rapport d'un import ou d'un essai d'œuvres : compteurs, puis lignes refusées et à vérifier. */
export function RapportRepriseVue({ rapport }: { readonly rapport: RapportOeuvres }): JSX.Element {
  const { t } = useTranslation('regie');
  const suffixe = rapport.essai ? 'Essai' : '';
  const details = Object.entries(rapport.details).filter(([, nombre]) => nombre > 0);
  const listes = Object.entries(rapport.listes).filter(([, valeurs]) => valeurs.length > 0);
  return (
    <div className={styles.rapport}>
      <h3>{t(`${CLE}.${rapport.essai ? 'titreEssai' : 'titre'}`)}</h3>
      <ul className={styles.compteurs}>
        <li>{t(`${CLE}.total`, { count: rapport.total })}</li>
        <li>{t(`${CLE}.crees${suffixe}`, { count: rapport.crees })}</li>
        {rapport.refuses > 0 ? <li>{t(`${CLE}.refuses`, { count: rapport.refuses })}</li> : null}
        {details.map(([nom, nombre]) => (
          <li key={nom}>
            {t(`${CLE}.details.${nom}${nom === 'imagesDeposees' ? suffixe : ''}`, {
              count: nombre,
            })}
          </li>
        ))}
      </ul>
      {listes.map(([nom, valeurs]) => (
        <p key={nom}>{t(`${CLE}.listes.${nom}${suffixe}`, { liste: valeurs.join(', ') })}</p>
      ))}
      {rapport.colonnesInconnues.length > 0 ? (
        <p>{t(`${CLE}.colonnesInconnues`, { liste: rapport.colonnesInconnues.join(', ') })}</p>
      ) : null}
      <Lignes
        titre={t(`${CLE}.erreursTitre`)}
        lignes={rapport.erreurs}
        classe={styles.listeErreurs}
      />
      <Lignes
        titre={t(`${CLE}.avertissementsTitre`)}
        lignes={rapport.avertissements}
        classe={styles.listeAvertissements}
      />
    </div>
  );
}
